import lodashForOwn from "lodash/forOwn";
import lodashUniqBy from "lodash/uniqBy";
import lodashGet from "lodash/get";

import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import StaticMode from "@mapbox/mapbox-gl-draw-static-mode";
import { centroid } from "@turf/turf";

import store from "@/store";
import { X_API_KEY } from "@/common/config";
import { getAccessToken, getUserGroups } from "@/common/storage";

import {
  DEFAULT_LAYERS_DETAILS,
  MAIN_MAP_ID,
  MAP_ATTRIBUTION,
} from "@/common/constants/maps";
import { ICONS } from "@/common/constants/icons";
import { MAP_ICON_NAMES } from "@/common/constants/maps";
import {
  PLACES_IMAGE_URL,
  STATIC_RESOURCES_PATH,
} from "@/common/constants/api";
import { addAcetate } from "@/common/maps/features";
import { LAYER_ID_ACETATE } from "@/common/constants/maps";
import { getMapDrawStyles } from "@/common/maps/styles";
import SearchControl from "@/common/maps/searchControl";
import ClearControl from "@/common/maps/clearControl";
import DistanceControl from "@/common/maps/distanceControl";

export { _initMap as initMap };
export { _flyTo as flyTo };
export { _addDrawControls as addDrawControls };
export { _removeDrawControls as removeDrawControls };
export { _isMainMap as isMainMap };
export { _getMap as getMap };
export { _loadIcons as loadIcons };
export { _convertMapToPng as convertMapToPng };
export { _waitForMapReady as waitForMapReady };

import { loadCategoryIcons as loadCrIncidentsCategoryIcons } from "@/services/controlRisks/incidents.service";
import { loadTravelerIcon } from "@/services/isosTravelTracker/travelTracker.service";
import { loadGroupMemberIcon } from "@/services/communication/communication.service.js";
import { loadMapIcons as loadAssetMapIcons } from "@/services/assetManagement/assets.service.js";
import { loadMapIcons as loadMyIncidentsMapIcons } from "@/services/myIncidents/myIncidents.service.js";
import { isFeatureUnlocked } from "@/common/featureLock";

const MAPS_GETTER = "map/maps";

mapboxgl.accessToken =
  "pk.eyJ1IjoiZ2Vvc3BhcmtpbyIsImEiOiJjamQzOWMxaTAwN2R0Mndud2ZyMXhyMnZhIn0.YyzTBjC4LvDFys_m-0ZlNA";

const _initMap = (mapOptions) => {
  let esriStyle = false;
  let esriSource;
  mapOptions.transformRequest = function (url, resourceType) {
    if (resourceType === "Style" && url.includes("api.mapbox.com")) {
      esriStyle = false;
      esriSource = undefined;
      return {
        url,
      };
    }
    if (
      (resourceType === "Source" || resourceType === "Tile") &&
      (url.includes("geospark.io") ||
        url.includes("seerist.com") ||
        url.includes("amazonaws") ||
        url.includes("localhost"))
    ) {
      return {
        url: url,
        headers: {
          Authorization: "Bearer " + getAccessToken(),
          "x-api-key": X_API_KEY,
        },
      };
    }
    if (
      (resourceType === "Style" && url.includes("www.arcgis.com")) ||
      url.includes(STATIC_RESOURCES_PATH)
    ) {
      esriStyle = true;
      return {
        url,
      };
    }
    if (url.indexOf("batdev") > -1) {
      return {
        url: url,
      };
    }
    if (esriStyle && resourceType === "Source") {
      esriSource = url;
      return {
        url: `${esriSource}?f=json`,
      };
    }
    if (esriSource && resourceType === "Tile") {
      if (!url.match("https://")) {
        return {
          url: `${esriSource}/${url}`,
        };
      }
      return {
        url,
      };
    }
  };
  mapOptions.preserveDrawingBuffer = true;
  mapOptions.renderWorldCopies = true;

  if (!mapOptions.style) {
    mapOptions.style = DEFAULT_LAYERS_DETAILS["esri_world_street"].style;
  }

  let baselayer = mapOptions.style;
  // if the user stills has the old mapbox baselayers switch them to the esri baselayer
  if (mapOptions.style.includes("mapbox://")) {
    baselayer = DEFAULT_LAYERS_DETAILS["esri_world_street"].style;
  }

  if (!mapOptions.center) {
    mapOptions.center = [0, 0];
  }
  if (!mapOptions.hasOwnProperty("zoom")) {
    mapOptions.zoom = 2;
  }

  mapOptions.attributionControl = false;

  let map = new mapboxgl.Map(mapOptions);
  map.boxZoom.enable();

  // set the default cursor when moving around the map
  map.on("mousemove", function () {
    map.getCanvas().style.cursor = "";
  });

  let attribution = MAP_ATTRIBUTION.vector;
  if (
    mapOptions.style === DEFAULT_LAYERS_DETAILS["custom_satellite"].style ||
    mapOptions.style === DEFAULT_LAYERS_DETAILS["custom_satellite_dark"].style
  ) {
    attribution += `<br>${MAP_ATTRIBUTION.imagery}`;
  }
  map.addControl(
    new mapboxgl.AttributionControl({
      customAttribution: attribution,
      compact: mapOptions.attributionCompact || false,
    }),
    "bottom-right"
  );

  if (!mapOptions.hideNavigationControls) {
    _addMapControls(map, "navigation");
  }
  if (!mapOptions.hideScaleControls) {
    _addMapControls(map, "scale");
  }
  if (mapOptions.showSearch) {
    _addMapControls(map, "search", mapOptions);
  } else if (mapOptions.showSearchWithKeyword) {
    _addMapControls(map, "search-with-keyword");
  }
  if (mapOptions.showDistance) {
    _addMapControls(map, "distance", mapOptions);
  }
  if (
    isFeatureUnlocked("map-events-ai-clear-button") &&
    !mapOptions.clearControls &&
    _isMainMap(map)
  ) {
    _addMapControls(map, "clear");
  }

  if (mapOptions.drawOptions) {
    _addDrawControls(map, mapOptions.drawOptions);
  }

  map.on("load", () => {
    // acetate layer is a blank layer used as a placeholder
    // between OUR polygon (like pulse) layers and point layers
    addAcetate(map);
  });

  return map;
};

const _addMapControls = (map, type, mapOptions) => {
  if (type === "navigation") {
    map.addControl(
      new mapboxgl.NavigationControl({ showCompass: true }),
      "top-left"
    );
  }

  if (type === "scale") {
    map.addControl(
      new mapboxgl.ScaleControl({
        unit: "imperial",
      }),
      "bottom-left"
    );
    map.addControl(
      new mapboxgl.ScaleControl({
        unit: "metric",
      }),
      "bottom-left"
    );
  }
  if (type === "search") {
    let options = null;
    if (mapOptions.openSearchOnMapLoad) {
      options = { openOnMapLoad: true };
    }
    map.addControl(new SearchControl(options), "top-left");
  }
  if (type === "search-with-keyword") {
    map.addControl(new SearchControl({ showKeyword: true }), "top-left");
  }
  if (type === "clear") {
    map.addControl(new ClearControl(), "top-left");
  }
  if (type === "distance") {
    let options = null;
    map.addControl(new DistanceControl(options), "top-left");
  }
};

const _flyTo = (feature, map) => {
  const CENTER = centroid(feature);
  map.flyTo({
    center: CENTER.geometry.coordinates,
  });
};

const _addDrawControls = (map, options) => {
  options.styles = getMapDrawStyles();
  var modes = MapboxDraw.modes;
  modes.static = StaticMode;
  options.modes = modes;
  const drawControl = new MapboxDraw(options);
  map.addControl(drawControl);

  if (options.createEvent) {
    map.on("draw.create", options.createEvent);
  }
  if (options.deleteEvent) {
    map.on("draw.delete", options.deleteEvent);
  }
  if (options.updateEvent) {
    map.on("draw.update", options.updateEvent);
  }
  if (options.selectionChangeEvent) {
    map.on("draw.selectionchange", options.selectionChangeEvent);
  }
  if (options.modeChangeEvent) {
    map.on("draw.modechange", options.modeChangeEvent);
  }
  if (options.renderEvent) {
    map.on("draw.render", options.renderEvent);
  }
  if (options.actionableEvent) {
    map.on("draw.actionable", options.actionableEvent);
  }
  return drawControl;
};

const _removeDrawControls = (map, drawControl) => {
  map.removeControl(drawControl);
};

const _isMainMap = (map) => {
  const MAPS = store.getters[MAPS_GETTER];
  if (MAPS) {
    const MAIN_MAP = MAPS[MAIN_MAP_ID];
    if (
      MAIN_MAP === undefined ||
      (MAIN_MAP && MAIN_MAP._mapId === map._mapId)
    ) {
      return true;
    }
  }
  return false;
};

const _getMap = (mapId) => {
  const MAPS = store.getters[MAPS_GETTER];
  if (MAPS && MAPS[mapId]) {
    return MAPS[mapId];
  }
  return null;
};

const _loadIcons = (map, places) => {
  const USER_GROUPS = getUserGroups();
  let icons = [];
  lodashForOwn(MAP_ICON_NAMES, (value, key) => {
    try {
      if (key === "events_general") {
        icons.push({
          name: value,
          value: ICONS.types["events_general"].active,
        });
      } else {
        icons.push({
          name: value,
          value: require("@/assets/images" + ICONS.types[key].active),
        });
      }
    } catch (err) {
      icons.push({
        name: value,
        value: ICONS.types["events_general"].active,
      });
    }
  });

  lodashForOwn(ICONS.places, (value, key) => {
    icons.push({
      name: key,
      value: require("@/assets/images" + ICONS.places[key].active),
    });
  });

  // need to load any custom icons in the user's groups
  if (places && Array.isArray(places) && places.length) {
    const URL = PLACES_IMAGE_URL;
    places.forEach((place) => {
      if (place && place.icons && place.icons.toLowerCase() !== "default") {
        if (USER_GROUPS) {
          USER_GROUPS.forEach((g) => {
            if (
              g &&
              g.toLowerCase().indexOf("user_") > -1 &&
              g.toLowerCase() !== "user_all" &&
              g.toLowerCase() === place.group.toLowerCase()
            ) {
              icons.push({
                name: place.icons,
                value: `${URL}/${g.toLowerCase()}/${place.icons}.png`,
              });
            }
          });
        }
      }
    });
  }

  icons = lodashUniqBy(icons, "name");

  for (let i = 0; i < icons.length; i++) {
    if (map.hasImage(icons[i].name)) {
      map.removeImage(icons[i].name);
    }
    map.loadImage(icons[i].value, (err, image) => {
      if (err) {
        console.error(err);
      } else {
        map.addImage(icons[i].name, image);
      }
    });
  }

  loadCrIncidentsCategoryIcons(map);
  loadTravelerIcon(map);
  loadGroupMemberIcon(map);
  loadAssetMapIcons(map);
  loadMyIncidentsMapIcons(map);
};

function _convertMapToPng(map) {
  return map.getCanvas().toDataURL("image/png", 1.0);
}

function _isMapLoading(map) {
  if (
    !map ||
    !map._fullyLoaded ||
    !map._loaded ||
    !map.style._loaded ||
    !map.isStyleLoaded()
  ) {
    return true;
  }
  return false;
}

function _isAcetateAdded(map) {
  try {
    let layers = map.getStyle().layers;
    for (let i = 0; i < layers.length; i++) {
      if (layers[i].id === LAYER_ID_ACETATE) {
        return true;
      }
    }
    return false;
  } catch (err) {
    console.error(err);
    return false;
  }
}

async function _waitForMapReady(map, params = {}, attempt = 0) {
  let interval = params.interval ? params.interval : 500;
  let maxAttempts = params.maxAttempts ? params.maxAttempts : 2000;

  let mapLoading = _isMapLoading(map);
  let acetateAdded = !mapLoading ? _isAcetateAdded(map) : false;

  if (acetateAdded) {
    return true;
  }

  if (attempt >= maxAttempts) {
    return false;
  }

  await new Promise((r) => setTimeout(r, interval));

  return await _waitForMapReady(map, interval, maxAttempts, attempt + 1);
}
